import { OcrQuotaPolicy, OcrQuotaSubjectType, type OcrQuotaSummary } from '../schemas'
import { OcrQuotaActions, type OcrQuotaActionProps } from './OcrQuotaActions'

type OcrUsageDetailsProps = OcrQuotaActionProps & {
  summary: OcrQuotaSummary
  isTrial: boolean
}

const numberFormatter = new Intl.NumberFormat()

export function OcrUsageDetails({ summary, isTrial, onActivateSubscription, onContactSupport }: OcrUsageDetailsProps) {
  const isOrganization = summary.subject_type === OcrQuotaSubjectType.Organization
  const unlimited = summary.policy === OcrQuotaPolicy.Unlimited
  const title = `OCR ${isTrial ? 'Trial ' : ''}Usage`
  const untracked = unlimited && summary.activation_at === null

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-brand-neutral-200 bg-white p-5" aria-label={title}>
        <h2 className="text-lg font-semibold text-brand-neutral-950">{title}</h2>
        {untracked ? (
          <p className="mt-4 text-sm text-brand-neutral-700">OCR is unlimited. Usage tracking has not been enabled for this account.</p>
        ) : (
          <dl className={`mt-5 grid grid-cols-1 gap-5 ${isOrganization ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-3'}`}>
            <div>
              <dt className="text-sm text-brand-neutral-600">Your pages used</dt>
              <dd className="mt-1 text-2xl font-semibold tabular-nums text-brand-neutral-950">
                {numberFormatter.format(isOrganization ? summary.own_contribution_pages : summary.counted_pages)}
              </dd>
            </div>
            {isOrganization && (
              <div>
                <dt className="text-sm text-brand-neutral-600">Organization pages used</dt>
                <dd className="mt-1 text-2xl font-semibold tabular-nums text-brand-neutral-950">{numberFormatter.format(summary.counted_pages)}</dd>
              </div>
            )}
            <div>
              <dt className="text-sm text-brand-neutral-600">Capacity</dt>
              <dd className="mt-1 text-2xl font-semibold tabular-nums text-brand-neutral-950">
                {unlimited ? 'Unlimited' : numberFormatter.format(summary.page_limit)}
              </dd>
            </div>
            {!unlimited && (
              <div>
                <dt className="text-sm text-brand-neutral-600">Pages remaining</dt>
                <dd className="mt-1 text-2xl font-semibold tabular-nums text-brand-neutral-950">{numberFormatter.format(summary.remaining_pages)}</dd>
              </div>
            )}
          </dl>
        )}
        <div className="mt-5 text-sm">
          <OcrQuotaActions onActivateSubscription={onActivateSubscription} onContactSupport={onContactSupport} />
        </div>
      </section>

      <section className="rounded-lg border border-brand-neutral-200 bg-white p-5" aria-label="Queue">
        <h2 className="text-lg font-semibold text-brand-neutral-950">Queue</h2>
        <dl className="mt-4 space-y-3 text-sm">
          <div className="flex flex-wrap justify-between gap-2">
            <dt>Waiting for OCR page quota</dt>
            <dd className="tabular-nums">{numberFormatter.format(summary.waiting.known_pages)} pages</dd>
          </div>
          {summary.admitted_unconsumed && (
            <div className="flex flex-wrap justify-between gap-2">
              <dt>Ready to start OCR</dt>
              <dd className="tabular-nums">{numberFormatter.format(summary.admitted_unconsumed.known_pages)} pages</dd>
            </div>
          )}
          <div className="flex flex-wrap justify-between gap-2">
            <dt>OCR in progress</dt>
            <dd className="tabular-nums">{numberFormatter.format(summary.released_unconsumed.known_pages)} pages</dd>
          </div>
        </dl>
        {summary.waiting.known_files > 0 && (
          <p className="mt-4 text-sm text-brand-neutral-600">Queued documents resume automatically when enough allowance becomes available.</p>
        )}
      </section>

      <p className="text-sm text-brand-neutral-600">
        OCR usage counts completed OCR pages, not all uploaded pages. Deleting files does not restore used pages.
      </p>
    </div>
  )
}
